import { Card, CardProps, ConfigProvider, Empty, Listy, ListyProps, ListyStyles, theme } from "antd";
import { CardSemanticType } from "antd/es/card/Card";
import { ComponentToken as ListyComponentToken } from "antd/es/listy/style";
import { GlobalToken } from "antd/es/theme/interface";
import { Key, ReactElement, ReactNode, Ref } from "react";

export type XmListySize = "small" | "middle";

export const LISTY_STYLES = {
  item: { backgroundColor: "transparent" },
} as const satisfies ListyStyles;

const getSizeToken = (
  { paddingXS, padding }: GlobalToken,
  size: XmListySize
): ListyComponentToken | undefined =>
  size === "small" ? { itemPaddingBlock: paddingXS, itemPaddingInline: padding } : undefined;

export interface XmListyProps<T, K extends Key = Key> extends Omit<ListyProps<T, K>, "styles"> {
  styles?: ListyStyles;
  size?: XmListySize;
  cardProps?: Omit<CardProps, "styles"> & { styles?: CardSemanticType["styles"] };
  /** Pass `null` to render nothing instead of the default `Empty`. */
  empty?: ReactNode;
  viewportRef?: Ref<HTMLDivElement>;
  viewportClassName?: string;
}

/** AntD `Listy` in a `Card`, with an empty state and rows that keep the card background. */
export const XmListy = <T, K extends Key = Key>({
  size = "middle",
  cardProps,
  empty,
  viewportRef,
  viewportClassName,
  styles,
  items,
  ...listyProps
}: XmListyProps<T, K>): ReactElement => {
  const { token } = theme.useToken();
  const renderList = (): ReactElement => {
    const mergedStyles: ListyStyles = {
      ...LISTY_STYLES,
      ...styles,
      item: { ...LISTY_STYLES.item, ...styles?.item },
    };
    const list = <Listy<T, K> items={items} styles={mergedStyles} {...listyProps} />;
    const sizeToken = getSizeToken(token, size);
    if (!sizeToken) return list;
    return <ConfigProvider theme={{ components: { Listy: sizeToken } }}>{list}</ConfigProvider>;
  };
  const renderEmpty = (): ReactNode => empty !== undefined ? empty : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <Card
      {...cardProps}
      styles={{ ...cardProps?.styles, body: { padding: 0, ...cardProps?.styles?.body } }}
    >
      <div ref={viewportRef} className={viewportClassName}>
        {items?.length ? renderList() : renderEmpty()}
      </div>
    </Card>
  );
};

export default XmListy;
